import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import PhoneNumberOnboarding from '../../components/PhoneNumberOnboarding';
import SubscribeOnboarding from '../../components/SubscribeOnboarding';
import AIComplianceWidget from '../../components/AIComplianceWidget';
import LocationOnboarding from '../../components/LocationOnboarding';
import FirstTouchPhotoOnboarding from '../../components/FirstTouchPhotoOnboarding';
import ContactsImportOnboarding from '../../components/ContactsImportOnboarding';
import ConfirmTouchpointsOnboarding from '../../components/ConfirmTouchpointsOnboarding';
import GoLiveOnboarding from '../../components/GoLiveOnboarding';
import { getAuthHeaders } from '../../lib/api';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEP_LABELS,
  isOnboardingComplete,
  resolveOnboardingStep,
} from '../../utils/onboarding';

const Register = () => {
  const navigate = useNavigate();
  const { signUp, reloadProfile, updateProfile, user: authUser, userProfile, loading: authLoading, profileReady } = useAuth();
  const [step, setStep] = useState(ONBOARDING_STEPS.ACCOUNT);
  const [hydrated, setHydrated] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    personalPhone: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [goLiveMeta, setGoLiveMeta] = useState({ wentLive: false, keptPaused: false });
  const initialTokenRef = useRef(null);
  const hydrateOnceRef = useRef(false);

  const effectiveUserId = user?.id || authUser?.id;
  const effectiveAgentName = formData.fullName || userProfile?.full_name || '';
  const a2pApproved =
    userProfile?.a2p_status === 'approved' || userProfile?.campaign_status === 'VERIFIED';

  const persistStep = useCallback(
    async (nextStep) => {
      setStep(nextStep);
      if (!authUser?.id && !user?.id) return;
      try {
        await updateProfile?.({ onboarding_step: nextStep });
      } catch (err) {
        console.warn('Could not persist onboarding step:', err);
      }
    },
    [authUser?.id, user?.id, updateProfile]
  );

  // Hydrate / resume onboarding for logged-in incomplete users (once)
  useEffect(() => {
    if (hydrateOnceRef.current) return;
    if (authLoading || (authUser && !profileReady)) return;

    if (!authUser) {
      hydrateOnceRef.current = true;
      setHydrated(true);
      return;
    }

    if (isOnboardingComplete(userProfile)) {
      hydrateOnceRef.current = true;
      navigate('/client-management', { replace: true });
      return;
    }

    const subResult = searchParams.get('subscription');
    if (subResult === 'success' || subResult === 'cancelled') {
      hydrateOnceRef.current = true;
      reloadProfile?.();
      persistStep(ONBOARDING_STEPS.SUBSCRIBE);
      const next = new URLSearchParams(searchParams);
      next.delete('subscription');
      setSearchParams(next, { replace: true });
      setHydrated(true);
      return;
    }

    hydrateOnceRef.current = true;
    const resumed = resolveOnboardingStep(userProfile);
    setStep(resumed <= ONBOARDING_STEPS.ACCOUNT ? ONBOARDING_STEPS.PHONE : resumed);
    setHydrated(true);
  }, [
    authUser,
    authLoading,
    profileReady,
    userProfile,
    navigate,
    searchParams,
    setSearchParams,
    reloadProfile,
    persistStep,
  ]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  };

  const handleSignUp = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData.email || !formData.password || !formData.fullName) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error: signUpError } = await signUp(
        formData.email,
        formData.password,
        formData.fullName
      );

      if (signUpError) {
        setError(signUpError.message || 'Failed to create account');
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        setUser(data.user);
        if (data?.session) {
          setSession(data.session);
          initialTokenRef.current = data.session.access_token;
        }

        if (formData.personalPhone?.trim()) {
          supabase
            .from('user_profiles')
            .update({ phone: formData.personalPhone.trim(), onboarding_step: ONBOARDING_STEPS.PHONE })
            .eq('id', data.user.id)
            .then(({ error: phoneErr }) => {
              if (phoneErr) console.warn('Could not save personal phone:', phoneErr.message);
            });
        } else {
          supabase
            .from('user_profiles')
            .update({ onboarding_step: ONBOARDING_STEPS.PHONE })
            .eq('id', data.user.id)
            .then(({ error: stepErr }) => {
              if (stepErr) console.warn('Could not save onboarding step:', stepErr.message);
            });
        }

        setStep(ONBOARDING_STEPS.PHONE);

        const accessToken = data.session?.access_token;
        const { email, password } = formData;
        setTimeout(async () => {
          try {
            const headers = {
              'Content-Type': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            };
            const confirmResponse = await fetch('/api/auth/confirm-email', {
              method: 'POST',
              headers,
              body: JSON.stringify({
                userId: data.user.id,
              }),
            });

            if (!confirmResponse.ok) {
              console.warn('Failed to auto-confirm email, but continuing with signup');
            }

            if (!accessToken && email && password) {
              const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password,
              });
              if (!signInError && signInData?.session) {
                setSession(signInData.session);
                initialTokenRef.current = signInData.session.access_token;
              }
            }
          } catch (confirmError) {
            console.warn('Error confirming email:', confirmError);
            if (!accessToken && email && password) {
              try {
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                  email,
                  password,
                });
                if (!signInError && signInData?.session) {
                  setSession(signInData.session);
                  initialTokenRef.current = signInData.session.access_token;
                }
              } catch (signInErr) {
                console.warn('Sign-in after confirm failed:', signInErr);
              }
            }
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneNumberComplete = () => persistStep(ONBOARDING_STEPS.SUBSCRIBE);
  const handleSubscribeComplete = () => persistStep(ONBOARDING_STEPS.COMPLIANCE);
  const handleAIComplianceComplete = () => persistStep(ONBOARDING_STEPS.LOCATION);

  const handleLocationComplete = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch('/api/trigger-onboarding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          agentId: effectiveUserId,
          agentName: effectiveAgentName,
        }),
      });

      if (!response.ok) {
        console.warn('Failed to trigger touchpoint onboarding, but continuing...');
      }
    } catch (err) {
      console.warn('Error triggering touchpoint onboarding:', err);
    }

    await persistStep(ONBOARDING_STEPS.PHOTO);
  };

  const handleFirstTouchPhotoComplete = () => persistStep(ONBOARDING_STEPS.CONTACTS);
  const handleContactsComplete = () => persistStep(ONBOARDING_STEPS.CONFIRM);
  const handleConfirmComplete = () => persistStep(ONBOARDING_STEPS.GO_LIVE);

  const handleGoLiveComplete = (meta) => {
    setGoLiveMeta({
      wentLive: Boolean(meta?.wentLive),
      keptPaused: Boolean(meta?.keptPaused),
    });
    persistStep(ONBOARDING_STEPS.WELCOME);
  };

  const handleComplete = async () => {
    try {
      await updateProfile?.({
        onboarding_step: ONBOARDING_STEPS.WELCOME,
        onboarding_completed_at: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('Could not mark onboarding complete:', err);
    }
    navigate('/client-management');
  };

  const renderStepIndicator = (currentStep) => (
    <div className="mb-6 text-center">
      <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-2 flex-wrap gap-y-1">
        {ONBOARDING_STEP_LABELS.map((label, i) => {
          const n = i + 1;
          const done = currentStep > n;
          const active = currentStep === n;
          return (
            <React.Fragment key={label}>
              {i > 0 && (
                <div className={`h-1 w-3 sm:w-6 ${done || active ? 'bg-primary' : 'bg-muted'}`} />
              )}
              <div
                className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold ${
                  done || active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                }`}
                title={label}
              >
                {n}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground">
        Step {Math.min(currentStep, ONBOARDING_STEP_LABELS.length)} of {ONBOARDING_STEP_LABELS.length}:{' '}
        {ONBOARDING_STEP_LABELS[currentStep - 1] || 'Done'}
      </p>
    </div>
  );

  if (!hydrated && (authLoading || (authUser && !profileReady))) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  // Step 1: Registration Form (only for brand-new visitors)
  if (step === ONBOARDING_STEPS.ACCOUNT) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="UserPlus" size={32} className="text-primary" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Create Your Account</h1>
            <p className="text-muted-foreground mt-2">
              Get started with Nurture Buddy.ai in just a few steps
            </p>
          </div>

          <form onSubmit={handleSignUp} className="space-y-6">
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            <Input
              type="text"
              label="Full Name"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              placeholder="Enter your full name"
              required
              disabled={isLoading}
            />

            <div>
              <Input
                type="tel"
                label="Your Mobile Number (optional)"
                value={formData.personalPhone}
                onChange={(e) => handleInputChange('personalPhone', e.target.value)}
                placeholder="+1 (555) 000-0000"
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                We&apos;ll text you here when a client replies to a touchpoint.
              </p>
            </div>

            <Input
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your email"
              required
              disabled={isLoading}
            />

            <Input
              type="password"
              label="Password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="Create a password (min. 8 characters)"
              required
              disabled={isLoading}
            />

            <Input
              type="password"
              label="Confirm Password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              placeholder="Confirm your password"
              required
              disabled={isLoading}
            />

            <Button type="submit" className="w-full" loading={isLoading} disabled={isLoading}>
              Create Account
            </Button>
          </form>

          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                Sign in
              </Link>
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy-policy" className="hover:text-foreground transition-smooth">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-foreground transition-smooth">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.PHONE) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(2)}
          <PhoneNumberOnboarding
            onComplete={handlePhoneNumberComplete}
            userId={effectiveUserId}
            accessToken={initialTokenRef.current || session?.access_token}
          />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.SUBSCRIBE) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(3)}
          <SubscribeOnboarding onComplete={handleSubscribeComplete} checkoutContext="onboarding" />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.COMPLIANCE) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(4)}
          <AIComplianceWidget
            onComplete={handleAIComplianceComplete}
            userId={effectiveUserId}
            showSkip
            checkoutContext="onboarding"
          />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.LOCATION) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(5)}
          <LocationOnboarding onComplete={handleLocationComplete} />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.PHOTO) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(6)}
          <FirstTouchPhotoOnboarding onComplete={handleFirstTouchPhotoComplete} />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.CONTACTS) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(7)}
          <ContactsImportOnboarding onComplete={handleContactsComplete} />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.CONFIRM) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(8)}
          <ConfirmTouchpointsOnboarding
            onComplete={handleConfirmComplete}
            firstTouchIntro={userProfile?.first_contact_intro_template}
            firstTouchImageUrl={userProfile?.first_contact_image_url}
          />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.GO_LIVE) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {renderStepIndicator(9)}
          <GoLiveOnboarding
            onComplete={handleGoLiveComplete}
            a2pApproved={a2pApproved}
            touchpointsPaused={userProfile?.touchpoints_paused !== false}
          />
        </div>
      </div>
    );
  }

  if (step === ONBOARDING_STEPS.WELCOME) {
    const paused = userProfile?.touchpoints_paused !== false;
    let welcomeDetail = "Your account is set up. We're generating your touchpoint messages…";
    if (goLiveMeta.keptPaused || (paused && !goLiveMeta.wentLive)) {
      welcomeDetail = 'Messaging is still paused — Resume anytime on Touchpoints.';
    } else if (!paused && !a2pApproved) {
      welcomeDetail =
        "Messaging is on — waiting on the legal stuff before texts go out (often 1–2 business days, sometimes up to ~10–15). We'll start automatically once that's done.";
    } else if (!paused && a2pApproved) {
      welcomeDetail =
        "Messaging is on. Monthly sends can run, and we'll text you when clients reply.";
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-foreground">Welcome to Nurture Buddy.ai!</h2>
          <p className="text-muted-foreground">{welcomeDetail}</p>
          <Button onClick={handleComplete} className="w-full">
            Get Started
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default Register;
